import { createI18n } from 'vue-i18n'
import enGB from './locales/en-GB.json'
import enUS from './locales/en-US.json'
import frFR from './locales/fr-FR.json'
import deDE from './locales/de-DE.json'

// Type definitions for better TypeScript support
export type MessageLanguages = {
  'en-GB': typeof enGB
  'en-US': typeof enUS
  'fr-FR': typeof frFR
  'de-DE': typeof deDE
}

export type MessageSchema = typeof enGB

// Configure i18n with default UK English locale for EU market
// TODO(region-parity): defaultLocale is hardcoded to 'en-GB' and fallbackLocale
// to 'en-US' — needs region-aware selection (device locale + user profile
// region) before we launch Pakistan/UAE. Leaving global change out of the
// glyph-sweep to avoid breaking every existing en-GB view in one pass.
const i18n = createI18n({
  legacy: false,
  locale: 'en-GB', // Default to UK English for EU region
  fallbackLocale: 'en-US',
  messages: {
    'en-GB': enGB,
    'en-US': enUS,
    'fr-FR': frFR,
    'de-DE': deDE
  },
  // NOTE(region-parity): the `currency` numberFormat here intentionally omits
  // a per-locale `currency:` default so every $n(value, 'currency', {...})
  // callsite MUST pass the entity's own ISO code — no silent GBP/USD/EUR
  // hardcode. Callsites that forget will throw; that is by design.
  numberFormats: {
    'en-GB': {
      currency: {
        style: 'currency',
        currencyDisplay: 'code'
      },
      decimal: {
        style: 'decimal',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      },
      percent: {
        style: 'percent',
        useGrouping: false
      }
    },
    'en-US': {
      currency: {
        style: 'currency',
        currencyDisplay: 'code'
      },
      decimal: {
        style: 'decimal',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      },
      percent: {
        style: 'percent',
        useGrouping: false
      }
    },
    'fr-FR': {
      currency: {
        style: 'currency',
        currencyDisplay: 'code'
      },
      decimal: {
        style: 'decimal',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      },
      percent: {
        style: 'percent',
        useGrouping: false
      }
    },
    'de-DE': {
      currency: {
        style: 'currency',
        currencyDisplay: 'code'
      },
      decimal: {
        style: 'decimal',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      },
      percent: {
        style: 'percent',
        useGrouping: false
      }
    }
  },
  datetimeFormats: {
    'en-GB': {
      short: {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      },
      long: {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        weekday: 'long'
      },
      time: {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false // 24-hour format for UK
      }
    },
    'en-US': {
      short: {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      },
      long: {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        weekday: 'long'
      },
      time: {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true // 12-hour format for US
      }
    },
    'fr-FR': {
      short: {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      },
      long: {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        weekday: 'long'
      },
      time: {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      }
    },
    'de-DE': {
      short: {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      },
      long: {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        weekday: 'long'
      },
      time: {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      }
    }
  }
})

export default i18n